import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AfClassification, SeqsimHit, SeqsimView } from '../../models/Seqsim';
import {
   flattenUbs,
   formatEValue,
   formatPercent,
   formatResolution,
   holoFraction,
   identityTone,
   probGap,
   topLigand,
   UbsFlatRow,
} from './seqsimFormat';

interface SeqsimResultsProps {
   hits: SeqsimHit[];
   view: SeqsimView;
}

type Dir = 'asc' | 'desc';

function missingLast(value: number | string | null | undefined): boolean {
   return value == null || value === '';
}

function compareValues(a: number | string | null | undefined, b: number | string | null | undefined, dir: Dir): number {
   if (missingLast(a) && missingLast(b)) return 0;
   if (missingLast(a)) return 1;
   if (missingLast(b)) return -1;
   const delta = typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b));
   return dir === 'asc' ? delta : -delta;
}

function SortHeader(props: {
   label: string;
   title: string;
   active: boolean;
   dir: Dir;
   onClick: () => void;
   className?: string;
}): React.JSX.Element {
   return (
      <th className={props.className} title={props.title}>
         <button type="button" className="seqsim-sort" onClick={props.onClick}>
            {props.label}
            {props.active && <span className="seqsim-sort-ind">{props.dir === 'asc' ? '▲' : '▼'}</span>}
         </button>
      </th>
   );
}

function AfChip({ classification }: { classification: AfClassification | null }): React.JSX.Element {
   const label = classification ?? 'unknown';
   return <span className={`seqsim-chip seqsim-chip--${label}`}>{label}</span>;
}

function IdentityCell({ identity }: { identity: number }): React.JSX.Element {
   const tone = identityTone(identity);
   const pct = Math.max(0, Math.min(100, identity * 100));
   return (
      <div className="seqsim-ident">
         <span>{formatPercent(identity, 1)}</span>
         <span className="seqsim-ident__bar">
            <span className={`seqsim-ident__fill seqsim-ident__fill--${tone}`} style={{ width: `${pct}%` }} />
         </span>
      </div>
   );
}

function BalanceCell({ fraction }: { fraction: number | null }): React.JSX.Element {
   if (fraction == null) return <span className="seqsim-missing">–</span>;
   const holo = Math.round(fraction * 100);
   return (
      <div className="seqsim-ah" title={`${holo}% of distinct PDBs caught holo`}>
         <span className="seqsim-ah__holo" style={{ width: `${holo}%` }} />
         <span className="seqsim-ah__apo" style={{ width: `${100 - holo}%` }} />
      </div>
   );
}

function UniProtCell({ hit }: { hit: SeqsimHit }): React.JSX.Element {
   return (
      <td>
         <div className="seqsim-uniprot">
            <a
               href={`https://www.uniprot.org/uniprotkb/${hit.uniprot_id}/entry`}
               target="_blank"
               rel="noopener noreferrer"
            >
               {hit.uniprot_id}
            </a>
            <Link to={`/db/ubs/${hit.uniprot_id}`} className="seqsim-ubs-link">
               UBS
            </Link>
            {hit.is_self && <span className="seqsim-self">exact match</span>}
         </div>
         {hit.target_seq_len != null && <div className="seqsim-meta">{hit.target_seq_len} aa</div>}
         {(hit.protein_name || hit.organism) && (
            <div className="seqsim-meta" title={[hit.protein_name, hit.organism].filter(Boolean).join(' · ')}>
               {hit.protein_name}
               {hit.protein_name && hit.organism && ' · '}
               {hit.organism && <em>{hit.organism}</em>}
            </div>
         )}
         {hit.note && <div className="seqsim-meta">{hit.note}</div>}
      </td>
   );
}

function LigandCell({ counts }: { counts: Record<string, number> }): React.JSX.Element {
   const top = topLigand(counts);
   if (!top) return <td className="seqsim-missing">–</td>;
   return (
      <td>
         <a href={`https://www.rcsb.org/ligand/${top.code}`} target="_blank" rel="noopener noreferrer">
            {top.code}
         </a>
         <span className="seqsim-meta"> ×{top.count}</span>
         {top.extra > 0 && <span className="seqsim-meta"> +{top.extra}</span>}
      </td>
   );
}

const SeqsimResults = ({ hits, view }: SeqsimResultsProps): React.JSX.Element => {
   const [sortKey, setSortKey] = useState<string | null>(null);
   const [sortDir, setSortDir] = useState<Dir>('desc');

   const toggle = (key: string, defaultDir: Dir = 'desc'): void => {
      if (sortKey !== key) {
         setSortKey(key);
         setSortDir(defaultDir);
         return;
      }
      setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
   };

   const sortedHits = useMemo(() => {
      if (!sortKey || view !== 'uniprot') return hits;
      const valueOf = (hit: SeqsimHit): number | string | null => {
         switch (sortKey) {
            case 'uniprot_id':
               return hit.uniprot_id;
            case 'seq_identity':
               return hit.seq_identity;
            case 'query_coverage':
               return hit.query_coverage;
            case 'alignment_length':
               return hit.alignment_length;
            case 'e_value':
               return hit.e_value;
            case 'num_target_pdbs':
               return hit.num_target_pdbs;
            case 'best_resolution':
               return hit.best_resolution;
            case 'num_apo_pdbs':
               return hit.num_apo_pdbs;
            case 'num_holo_pdbs':
               return hit.num_holo_pdbs;
            case 'holo_fraction':
               return holoFraction(hit.num_holo_pdbs, hit.num_pdbs_either);
            case 'ligand':
               return topLigand(hit.ligand_counts)?.code ?? null;
            case 'num_entries':
               return hit.num_entries;
            case 'num_ubs':
               return hit.num_ubs;
            case 'af_classification':
               return hit.af_classification;
            default:
               return null;
         }
      };
      return [...hits].sort((a, b) => compareValues(valueOf(a), valueOf(b), sortDir));
   }, [hits, sortKey, sortDir, view]);

   const ubsRows = useMemo(() => {
      const rows = flattenUbs(hits);
      if (!sortKey || view !== 'ubs') return rows;
      const valueOf = (row: UbsFlatRow): number | string | null => {
         switch (sortKey) {
            case 'uniprot_id':
               return row.hit.uniprot_id;
            case 'cluster_id':
               return row.ubs.cluster_id;
            case 'seq_identity':
               return row.hit.seq_identity;
            case 'classification':
               return row.ubs.classification;
            case 'probability':
               return row.ubs.probability;
            case 'prob_delta':
               return probGap(row.ubs);
            case 'site_size':
               return row.ubs.site_size;
            case 'num_cluster_entries':
               return row.ubs.num_cluster_entries;
            case 'ligand':
               return row.ubs.ligand;
            case 'num_apo':
               return row.ubs.num_apo;
            case 'num_holo':
               return row.ubs.num_holo;
            case 'holo_fraction_ubs': {
               const apo = row.ubs.num_apo ?? 0;
               const holo = row.ubs.num_holo ?? 0;
               return holoFraction(holo, apo + holo);
            }
            default:
               return null;
         }
      };
      return [...rows].sort((a, b) => compareValues(valueOf(a), valueOf(b), sortDir));
   }, [hits, sortKey, sortDir, view]);

   const header = (
      key: string,
      label: string,
      title: string,
      className?: string,
      defaultDir: Dir = 'desc',
   ): React.JSX.Element => (
      <SortHeader
         label={label}
         title={title}
         className={className}
         active={sortKey === key}
         dir={sortDir}
         onClick={(): void => toggle(key, defaultDir)}
      />
   );

   if (view === 'ubs') {
      return (
         <div className="seqsim-scroll">
            <table className="seqsim-table">
               <thead>
                  <tr>
                     {header('uniprot_id', 'UniProt', 'Matched accession', undefined, 'asc')}
                     {header('cluster_id', 'UBS', 'Cluster id at threshold 0.5', undefined, 'asc')}
                     {header('seq_identity', 'Identity', 'Sequence identity of the hit')}
                     {header('classification', 'AF', 'AlphaFold apo/holo label for this cluster', undefined, 'asc')}
                     {header('probability', 'P', 'Higher of the mean apo and holo probabilities')}
                     {header('prob_delta', 'Gap', 'Absolute difference between holo and apo probability')}
                     {header('site_size', 'Site', 'Residues in the union of the cluster')}
                     {header('num_cluster_entries', 'Entries', 'Structures in this cluster')}
                     {header('ligand', 'Ligand', 'Representative ligand', undefined, 'asc')}
                     {header('num_apo', 'Apo', 'Apo observations in the cluster')}
                     {header('num_holo', 'Holo', 'Holo observations in the cluster')}
                     {header('holo_fraction_ubs', 'A:H', 'Share of observations that are holo')}
                  </tr>
               </thead>
               <tbody>
                  {ubsRows.length === 0 && (
                     <tr>
                        <td colSpan={12} className="seqsim-empty-cell">
                           None of the hits have a UBS cluster at this threshold.
                        </td>
                     </tr>
                  )}
                  {ubsRows.map((row) => {
                     const apo = row.ubs.num_apo ?? 0;
                     const holo = row.ubs.num_holo ?? 0;
                     return (
                        <tr
                           key={`${row.hit.uniprot_id}-${row.ubs.cluster_id}`}
                           className={row.hit.is_self ? 'seqsim-row-self' : undefined}
                        >
                           <UniProtCell hit={row.hit} />
                           <td>
                              <Link to={`/db/ubs/${row.hit.uniprot_id}`}>{row.ubs.cluster_id}</Link>
                              {row.ubs.representative_entry && (
                                 <div className="seqsim-meta">
                                    <Link to={`/db/entry/${row.ubs.representative_entry}`}>representative</Link>
                                 </div>
                              )}
                           </td>
                           <td>
                              <IdentityCell identity={row.hit.seq_identity} />
                           </td>
                           <td>
                              <AfChip classification={row.ubs.classification} />
                           </td>
                           <td className="num">{row.ubs.probability == null ? '–' : row.ubs.probability.toFixed(2)}</td>
                           <td className="num">{probGap(row.ubs)?.toFixed(2) ?? '–'}</td>
                           <td className="num">{row.ubs.site_size ?? '–'}</td>
                           <td className="num">{row.ubs.num_cluster_entries ?? '–'}</td>
                           <td>
                              {row.ubs.ligand ? (
                                 <a
                                    href={`https://www.rcsb.org/ligand/${row.ubs.ligand}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                 >
                                    {row.ubs.ligand}
                                 </a>
                              ) : (
                                 '–'
                              )}
                              {row.ubs.ligands.length > 1 && (
                                 <span className="seqsim-meta"> +{row.ubs.ligands.length - 1}</span>
                              )}
                           </td>
                           <td className="num">{apo}</td>
                           <td className="num">{holo}</td>
                           <td>
                              <BalanceCell fraction={holoFraction(holo, apo + holo)} />
                           </td>
                        </tr>
                     );
                  })}
               </tbody>
            </table>
         </div>
      );
   }

   return (
      <div className="seqsim-scroll">
         <table className="seqsim-table">
            <thead>
               <tr>
                  {header('uniprot_id', 'UniProt', 'Matched accession', undefined, 'asc')}
                  {header('seq_identity', 'Identity', 'Fraction of aligned positions that match')}
                  {header('query_coverage', 'Coverage', 'Fraction of the query covered by the alignment')}
                  {header('alignment_length', 'Aln', 'Alignment length in residues')}
                  {header('e_value', 'E-value', 'Smaller means a less likely chance alignment')}
                  {header('num_target_pdbs', 'PDBs', 'Distinct target PDB structures in AHoJ-DB')}
                  {header(
                     'best_resolution',
                     'Res',
                     'Best (lowest) resolution among those structures. Lower sorts first.',
                     undefined,
                     'asc',
                  )}
                  {header('num_apo_pdbs', 'Apo', 'Distinct PDBs with a ligand-free chain')}
                  {header('num_holo_pdbs', 'Holo', 'Distinct PDBs with a ligand-bound chain')}
                  {header('holo_fraction', 'A:H', 'Share of distinct PDBs caught holo')}
                  {header('ligand', 'Ligand', 'Most frequent bound ligand', undefined, 'asc')}
                  {header('num_entries', 'Entries', 'AHoJ-DB entries for this UniProt')}
                  {header('num_ubs', 'UBS', 'Unified binding sites at threshold 0.5')}
                  {header('af_classification', 'AF', 'Aggregate AlphaFold apo/holo label', undefined, 'asc')}
               </tr>
            </thead>
            <tbody>
               {sortedHits.map((hit) => (
                  <tr key={hit.uniprot_id} className={hit.is_self ? 'seqsim-row-self' : undefined}>
                     <UniProtCell hit={hit} />
                     <td>
                        <IdentityCell identity={hit.seq_identity} />
                     </td>
                     <td
                        className="num"
                        title={
                           hit.target_coverage == null
                              ? undefined
                              : `Target coverage ${formatPercent(hit.target_coverage)}`
                        }
                     >
                        {formatPercent(hit.query_coverage)}
                     </td>
                     <td className="num">{hit.alignment_length}</td>
                     <td className="num">{formatEValue(hit.e_value)}</td>
                     <td className="num">{hit.num_target_pdbs}</td>
                     <td className="num">{formatResolution(hit.best_resolution)}</td>
                     <td className="num">{hit.num_apo_pdbs}</td>
                     <td className="num">{hit.num_holo_pdbs}</td>
                     <td>
                        <BalanceCell fraction={holoFraction(hit.num_holo_pdbs, hit.num_pdbs_either)} />
                     </td>
                     <LigandCell counts={hit.ligand_counts} />
                     <td className="num">
                        {hit.num_entries > 0 ? (
                           <Link to={`/db?uniprot_ids=${hit.uniprot_id}`}>{hit.num_entries}</Link>
                        ) : (
                           0
                        )}
                     </td>
                     <td className="num">
                        {hit.num_ubs > 0 ? <Link to={`/db/ubs/${hit.uniprot_id}`}>{hit.num_ubs}</Link> : 0}
                     </td>
                     <td>
                        <AfChip classification={hit.af_classification} />
                        {hit.af_per_ubs.length > 0 && (
                           <div className="seqsim-chips">
                              {hit.af_per_ubs.slice(0, 6).map((ubs) => (
                                 <Link
                                    key={ubs.cluster_id}
                                    to={`/db/ubs/${hit.uniprot_id}`}
                                    className={`seqsim-chip seqsim-chip--${ubs.classification ?? 'unknown'}`}
                                    title={ubs.cluster_id}
                                 >
                                    {ubs.cluster_id}
                                 </Link>
                              ))}
                              {hit.af_per_ubs.length > 6 && (
                                 <span className="seqsim-meta">+{hit.af_per_ubs.length - 6}</span>
                              )}
                           </div>
                        )}
                     </td>
                  </tr>
               ))}
            </tbody>
         </table>
      </div>
   );
};

export default SeqsimResults;
